import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark";
type ThemePref = Theme | "system";

interface ThemeCtx {
  theme: Theme;
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);
const STORAGE_KEY = "tcgpricer.theme";

function getSystem(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(t: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", t === "dark");
  root.style.colorScheme = t;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPref] = useState<ThemePref>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemePref | null;
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  });
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemePref | null;
    if (stored === "light" || stored === "dark") return stored;
    return getSystem();
  });

  useEffect(() => {
    const resolved: Theme = pref === "system" ? getSystem() : pref;
    setTheme(resolved);
    applyTheme(resolved);
    localStorage.setItem(STORAGE_KEY, pref);
  }, [pref]);

  useEffect(() => {
    if (pref !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const sys = getSystem();
      setTheme(sys);
      applyTheme(sys);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [pref]);

  const toggle = () => setPref(theme === "dark" ? "light" : "dark");

  return <Ctx.Provider value={{ theme, pref, setPref, toggle }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTheme must be used within ThemeProvider");
  return c;
}
